import { useEffect, useId, useMemo, useRef, useState } from 'react';

export interface TargetOption {
  id: string; // `screen:<id>` | `group:<id>`
  label: string;
  kind: 'screen' | 'group';
  sub?: string; // secondary line (location / member count)
}

/**
 * Compact, keyboard-accessible combobox for choosing which screen or group the
 * week calendar shows. Type to filter; ↑/↓ to move; Enter to pick; Esc to close.
 */
export function TargetPicker({
  options,
  value,
  onChange,
}: {
  options: TargetOption[];
  value: string | null;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const selected = options.find((o) => o.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.sub?.toLowerCase().includes(q));
  }, [options, query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(Math.max(0, options.findIndex((o) => o.id === value)));
    inputRef.current?.focus();
  }, [open, options, value]);

  const commit = (opt: TargetOption | undefined) => {
    if (!opt) return;
    onChange(opt.id);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(filtered.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(filtered[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="target-picker" ref={rootRef}>
      <button
        type="button"
        className="target-picker-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {selected ? (
          <span className="row" style={{ gap: 6 }}>
            <span className={`tp-kind ${selected.kind}`}>{selected.kind === 'group' ? 'Group' : 'Screen'}</span>
            <span className="cell-strong">{selected.label}</span>
          </span>
        ) : (
          <span className="text-dim">Pick a screen or group…</span>
        )}
        <span aria-hidden className="tp-caret">▾</span>
      </button>

      {open && (
        <div className="target-picker-pop">
          <input
            ref={inputRef}
            type="text"
            className="tp-search"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            placeholder="Search screens and groups…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />
          <ul className="tp-list" id={listId} role="listbox" aria-label="Screens and groups">
            {filtered.length === 0 && <li className="tp-empty">No matches</li>}
            {filtered.map((o, i) => (
              <li
                key={o.id}
                role="option"
                aria-selected={o.id === value}
                className={`tp-option${i === active ? ' active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(o);
                }}
              >
                <span className={`tp-kind ${o.kind}`}>{o.kind === 'group' ? 'Group' : 'Screen'}</span>
                <span className="tp-label">
                  <span className="cell-strong">{o.label}</span>
                  {o.sub && <span className="tp-sub">{o.sub}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
